import { IAngularEvent, IRootScopeService } from "angular";
import { IModalScope, IModalService } from "angular-ui-bootstrap";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { generateDefaultMandatoryOkrAlignmentAccountSettings } from "@gtmhub/configuration/utils/utils";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { GoalModalsService } from "@gtmhub/goals/services/goal-modals.service";
import { CloneOrMoveResourceService } from "@gtmhub/okrs/components/clone-okrs/clone-or-move-resource.service";
import { GoalsChangedEventArgs, OkrEvents, goalSelected } from "@gtmhub/okrs/events";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { filterActiveAndFutureSessions } from "@gtmhub/sessions/redux/session-selectors";
import { INgRedux } from "@gtmhub/state-management";
import { getCurrentUserId } from "@gtmhub/users";
import { IMandatoryOkrAlignmentSettings } from "@webapp/configuration/models/configuration.model";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { Session } from "@webapp/sessions/models/sessions.model";
import { getSessionAndOneLevelChildren } from "@webapp/sessions/utils/utils";
import { IGoal } from "../models";

export interface IConfirmCloneOrMoveModal {
  cfCounter: number;
  method: MethodType;
  invokerType: string; // "OKR" or "KR", but is not limited
}

export interface IGoalCloneSelected {
  session: Session;
  goal: IGoal;
  progress: boolean;
  cloneTasks: boolean;
  cloneChildren: boolean;
  moveChildren: boolean;
  notifications: boolean;
  duplicateInsight: boolean;
}

interface IGenericSessionSelectorScope extends IModalScope {
  selectSessionList(session: Session): void;

  selected: { screen: string };
  activeSessions: Session[];
}

export type MethodType = "clone" | "move";

interface ICloneSessionsRootScope extends IRootScopeService {
  renderDir: boolean;
}

export interface ICloneOrMoveGoalScope extends IModalScope {
  goal: IGoal;
  selected: IGoalCloneSelected;
  showSessionSelector: boolean;
  hasOnlyManualMetrics: boolean;
  activeSessions: Session[];

  prepareToMove();

  openGoalSelectorModal(method: MethodType): void;

  openSessionSelectorModal(): void;

  chooseBetweenSessions: boolean;
}

export class CloneOrMoveGoalCtrl {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$uibModal",
    "goal",
    "GoalModalsService",
    "$ngRedux",
    "FeatureTogglesFacade",
    "AccountService",
    "CloneOrMoveResourceService",
  ];

  constructor(
    private $rootScope: ICloneSessionsRootScope & ITraceRootScopeService,
    private $scope: ICloneOrMoveGoalScope,
    private $uibModal: IModalService,
    private goal: IGoal,
    private goalModalsService: GoalModalsService,
    private $ngRedux: INgRedux,
    private featureToggleService: FeatureTogglesFacade,
    private accountService: AccountService,
    private cloneOrMoveResourceService: CloneOrMoveResourceService
  ) {
    this.okrEvents = new OkrEvents($rootScope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
    this.init();
  }

  private okrEvents: OkrEvents;
  private isOkrAlignmentMandatory: boolean;

  private init = () => {
    this.$rootScope.traceAction("clone_or_move_goal_modal_init", () => {
      const mapStateToScope = (state: ISessionsStoreState) => {
        return {
          activeSessions: filterActiveAndFutureSessions(state.sessions.items),
        };
      };
      const unsubscribe = this.$ngRedux.connect(mapStateToScope)(this.$scope);
      this.$scope.$on("$destroy", unsubscribe);

      this.$scope.selected = {
        session: {},
        goal: {},
        progress: true,
        cloneTasks: false,
        cloneChildren: false,
        moveChildren: false,
        notifications: false,
        duplicateInsight: false,
      };

      // we need to check if there are any dynamic metrics in this goal
      if (this.goal.metrics && this.goal.metrics.length) {
        this.$scope.hasOnlyManualMetrics = this.goal.metrics.every((metric) => !metric.insightName);
      } else {
        this.$scope.hasOnlyManualMetrics = true;
      }

      this.$scope.prepareToMove = this.prepareToMove;
      this.$scope.openGoalSelectorModal = this.openGoalSelectorModal;
      this.$scope.openSessionSelectorModal = this.openSessionSelectorModal;

      this.$scope.goal = this.goal;

      this.featureToggleService.isFeatureAvailable(FeatureFlag.MandatoryOkrAlignmentFeature).then((isFeatureEnabled) => {
        const mandatoryOkrAlignmentSettings =
          this.accountService.getAccountSetting<IMandatoryOkrAlignmentSettings>("mandatoryOkrAlignment") || generateDefaultMandatoryOkrAlignmentAccountSettings();
        this.isOkrAlignmentMandatory =
          isFeatureEnabled && mandatoryOkrAlignmentSettings.enabled && !mandatoryOkrAlignmentSettings.excludedUserIds.includes(getCurrentUserId());
      });
    });
  };

  private validSessions: Session[];

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === goalSelected) {
      this.onGoalSelected(args.detail.goal);
    }
  };

  private onGoalSelected = (goal: IGoal) => {
    this.$scope.selected.goal = goal;
    this.$scope.selected.session = {};
    this.validSessions = getSessionAndOneLevelChildren(this.$scope.activeSessions, goal.sessionId);
    this.$scope.showSessionSelector = true;

    if (this.validSessions.length === 1) {
      this.$scope.selected.session = this.validSessions[0];
      this.$scope.selected.goal.sessionId = this.$scope.selected.session.id;
      this.$scope.chooseBetweenSessions = false;
    } else {
      this.$scope.chooseBetweenSessions = true;
    }
  };

  private openGoalSelectorModal = (method: MethodType): void => {
    this.$rootScope.traceAction("open_goal_selector_modal", () => {
      const canSelectSameGoalAsParent = this.shouldAllowToSelectSameGoalAsParent(method);
      this.$scope.selected.goal = null;
      this.$scope.selected.session = null;

      const sessionSelected = (event: IAngularEvent, session: Session) => {
        this.$scope.showSessionSelector = false;
        this.$scope.selected.session = session;
      };

      this.$scope.$on("sessionSelected", sessionSelected);

      const unselectableId = canSelectSameGoalAsParent ? null : this.$scope.goal.id;
      this.goalModalsService.openGoalSelectModal({
        unselectableId,
        invokedBy: "CloneOKR",
        enableOkrSelectionOnly: this.isOkrAlignmentMandatory,
      });
    });
  };

  private shouldAllowToSelectSameGoalAsParent = (method: MethodType): boolean => method === "clone";

  private openSessionSelectorModal = (): void => {
    this.$rootScope.traceAction("open_session_selector_modal", () => {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const ctrl = this;
      this.$uibModal.open({
        template: require("../views/generic-goal-selector.html"),
        windowClass: "medium",
        controller: [
          "$scope",
          function ($scope: IGenericSessionSelectorScope) {
            $scope.activeSessions = ctrl.validSessions;

            $scope.selectSessionList = (session: Session): void => {
              ctrl.$scope.selected.session = session;
              ctrl.$scope.selected.goal.sessionId = session.id;
              $scope.$dismiss();
            };
          },
        ],
      });
    });
  };

  private prepareToMove = (): void => {
    const m = this.goal.customFields;
    const cfCounter = m ? Object.keys(m).length : 0;
    if (cfCounter != 0) {
      this.cloneOrMoveResourceService.confirmAction("move", cfCounter).then(() => {
        this.move();
      });
    } else {
      this.move();
    }
  };

  private move = (): void => {
    this.$rootScope.traceAction(`prepare_to_move`, () => {
      this.cloneOrMoveResourceService.moveGoal(this.$scope.goal, this.$scope.selected).then(() => {
        this.$scope.$close();
      });
    });
  };
}
